import { useEffect, useMemo, useState } from 'react';
import api from '../api';

function Stock() {
  const [stock, setStock] = useState([]);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortBy, setSortBy] = useState('daysLeftAsc');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  const [quickOrderQty, setQuickOrderQty] = useState({});
  const [updatingAds, setUpdatingAds] = useState({});

  const [newProduct, setNewProduct] = useState({
    id: '',
    productName: '',
    quantity: '',
    inTransit: '',
    barcode: ''
  });

  const [editingProductId, setEditingProductId] = useState(null);
  const [editProduct, setEditProduct] = useState({
    id: '',
    productName: '',
    quantity: '',
    inTransit: '',
    barcode: ''
  });

  useEffect(() => {
    fetchStock();
  }, []);

  const fetchStock = async () => {
    try {
      setLoading(true);
      setError('');

      const response = await api.get('/getStock');
      setStock(response.data.stock || []);
    } catch (err) {
      setError(
        (err && err.response && err.response.data && err.response.data.error) ||
          err.message ||
          'Nu am putut incarca stocurile'
      );
    } finally {
      setLoading(false);
    }
  };

  const handleAddProduct = async (e) => {
    e.preventDefault();

    if (!newProduct.id.trim() || !newProduct.productName.trim()) {
      setError('Codul produsului si numele produsului sunt obligatorii');
      setMessage('');
      return;
    }

    try {
      setError('');
      setMessage('');

      await api.post('/addStock', {
        id: newProduct.id.trim(),
        productName: newProduct.productName.trim().slice(0, 80),
        quantity: Number(newProduct.quantity || 0),
        inTransit: Number(newProduct.inTransit || 0),
        barcode: (newProduct.barcode || newProduct.id).trim()
      });

      setMessage('Produs adaugat: ' + newProduct.productName);
      setNewProduct({
        id: '',
        productName: '',
        quantity: '',
        inTransit: '',
            barcode: ''
      });

      fetchStock();
    } catch (err) {
      setError(
        (err && err.response && err.response.data && err.response.data.error) ||
          err.message ||
          'Nu am putut adauga produsul'
      );
      setMessage('');
    }
  };

  const startEditProduct = (item) => {
    setEditingProductId(item.id);
    setEditProduct({
      id: item.id,
      productName: item.productName || '',
      quantity: String(item.quantity || 0),
      inTransit: String(item.inTransit || 0),
      barcode: item.barcode || item.id || ''
    });
    setError('');
    setMessage('');
  };

  const cancelEditProduct = () => {
    setEditingProductId(null);
    setEditProduct({
      id: '',
      productName: '',
      quantity: '',
      inTransit: '',
        barcode: ''
    });
  };

  const saveEditProduct = async () => {
    if (!editProduct.id.trim() || !editProduct.productName.trim()) {
      setError('Codul si numele produsului sunt obligatorii');
      setMessage('');
      return;
    }

    try {
      setError('');
      setMessage('');

      await api.patch('/changeStock', {
        id: editProduct.id.trim(),
        productName: editProduct.productName.trim().slice(0, 80),
        quantity: Number(editProduct.quantity || 0),
        inTransit: Number(editProduct.inTransit || 0),
        barcode: (editProduct.barcode || editProduct.id).trim()
      });

      setMessage('Produs actualizat: ' + editProduct.productName);
      cancelEditProduct();
      fetchStock();
    } catch (err) {
      setError(
        (err && err.response && err.response.data && err.response.data.error) ||
          err.message ||
          'Nu am putut actualiza produsul'
      );
      setMessage('');
    }
  };

  const handleQuickOrder = async (item) => {
    const qty = Number(quickOrderQty[item.id] || item.recommendedOrderQty || 0);

    if (!qty || qty <= 0) {
      setError('Introdu o cantitate valida pentru comanda');
      setMessage('');
      return;
    }

    try {
      setError('');
      setMessage('');

      await api.post('/orderSupplier', {
        identifier: item.id,
        quantity: qty
      });

      setMessage('Comanda marcata pentru ' + item.productName + ': ' + qty + ' buc');
      setQuickOrderQty((prev) => ({ ...prev, [item.id]: '' }));

      fetchStock();
    } catch (err) {
      setError(
        (err && err.response && err.response.data && err.response.data.error) ||
          err.message ||
          'Nu am putut marca produsul ca pe drum'
      );
      setMessage('');
    }
  };

  const handleDeleteProduct = async (item) => {
    const confirmed = window.confirm(
      'Sigur vrei sa stergi produsul "' + item.productName + '" (' + item.id + ')?'
    );

    if (!confirmed) {
      return;
    }

    try {
      setError('');
      setMessage('');

      await api.delete('/deleteStock', {
        data: {
          id: item.id,
          productName: item.productName,
          barcode: item.barcode || item.id
        }
      });

      setMessage('Produs sters: ' + item.productName);
      fetchStock();
    } catch (err) {
      setError(
        (err && err.response && err.response.data && err.response.data.error) ||
          err.message ||
          'Nu am putut sterge produsul'
      );
      setMessage('');
    }
  };

  const filteredStock = useMemo(() => {
    let items = [...stock];

    const q = search.trim().toLowerCase();

    if (q) {
      items = items.filter((item) =>
        String(item.id || '').toLowerCase().includes(q) ||
        String(item.productName || '').toLowerCase().includes(q) ||
        String(item.barcode || '').toLowerCase().includes(q)
      );
    }

    if (statusFilter !== 'all') {
      if (statusFilter === 'needsOrder') {
        items = items.filter((item) => item.needsOrder && Number(item.recommendedOrderQty || 0) >= 3);
      } else {
        items = items.filter((item) => (Number(item.quantity || 0) > 0 && item.offlineFlag === true) ? 'Revenit in stoc / repune pe eMag' : item.status === statusFilter);
      }
    }

    items.sort((a, b) => {
      if (sortBy === 'stockAsc') {
        return Number(a.quantity || 0) - Number(b.quantity || 0);
      }

      if (sortBy === 'stockDesc') {
        return Number(b.quantity || 0) - Number(a.quantity || 0);
      }

      if (sortBy === 'salesDesc') {
        return Number(b.salesLast10Days || 0) - Number(a.salesLast10Days || 0);
      }

      if (sortBy === 'futureStockAsc') {
        return Number(a.futureStock || 0) - Number(b.futureStock || 0);
      }

      if (sortBy === 'futureStockDesc') {
        return Number(b.futureStock || 0) - Number(a.futureStock || 0);
      }

      if (sortBy === 'recommendedDesc') {
        return Number(b.recommendedOrderQty || 0) - Number(a.recommendedOrderQty || 0);
      }

      if (sortBy === 'nameAsc') {
        return String(a.productName || '').localeCompare(String(b.productName || ''), 'ro');
      }

      if (sortBy === 'nameDesc') {
        return String(b.productName || '').localeCompare(String(a.productName || ''), 'ro');
      }

      const aDays = a.daysLeft === null ? Number.POSITIVE_INFINITY : Number(a.daysLeft);
      const bDays = b.daysLeft === null ? Number.POSITIVE_INFINITY : Number(b.daysLeft);

      if (sortBy === 'daysLeftDesc') {
        return bDays - aDays;
      }

      return aDays - bDays;
    });

    return items;
  }, [stock, search, statusFilter, sortBy]);

  const needsOrderItems = useMemo(() => {
    return [...stock]
      .filter((item) => item.needsOrder && Number(item.recommendedOrderQty || 0) >= 3)
      .sort((a, b) => Number(b.recommendedOrderQty || 0) - Number(a.recommendedOrderQty || 0));
  }, [stock]);

  const summary = useMemo(() => {
    const urgent = stock.filter((item) => item.status === 'Comanda urgent').length;
    const outOfStock = stock.filter((item) => item.status === 'Fara stoc').length;
    const onTheWay = stock.filter((item) => item.status === 'Pe drum').length;
    const onTheWayInsufficient = stock.filter((item) => item.status === 'Pe drum insuficient').length;
    const needsOrder = stock.filter((item) => item.needsOrder).length;

    return {
      total: stock.length,
      urgent: urgent,
      outOfStock: outOfStock,
      onTheWay: onTheWay,
      onTheWayInsufficient: onTheWayInsufficient,
      needsOrder: needsOrder
    };
  }, [stock]);


  const isMobile = typeof window !== 'undefined' ? window.innerWidth <= 768 : false;

  const stickyHeaderStyle = {
    padding: '12px 14px',
    textAlign: 'left',
    borderBottom: '1px solid #d1d5db',
    fontSize: '14px',
    fontWeight: 700,
    color: '#111827',
    position: 'sticky',
    top: 0,
    zIndex: 20,
    background: '#f3f4f6',
    boxShadow: '0 1px 0 #d1d5db'
  };

  const responsiveCardStyle = {
    ...cardStyle,
    padding: isMobile ? '12px' : cardStyle.padding
  };

  const responsiveTableWrapStyle = {
    ...tableWrapStyle,
    overflowX: 'auto',
    overflowY: 'auto',
    maxHeight: isMobile ? '68vh' : '74vh'
  };

  const responsiveTableStyle = {
    width: '100%',
    borderCollapse: 'collapse',
    minWidth: isMobile ? '1100px' : '1250px'
  };

  const responsiveInputStyle = {
    ...inputStyle,
    minWidth: isMobile ? '70px' : (inputStyle.minWidth || '110px')
  };

  const handleToggleEmagAds = async (item, checked) => {
    try {
      setUpdatingAds((prev) => ({ ...prev, [item.id]: true }));
      setError('');

      await api.post('/toggleEmagAds', {
        id: item.id,
        emagAds: checked
      });

      setStock((prev) =>
        prev.map((row) =>
          row.id === item.id ? { ...row, emagAds: checked } : row
        )
      );
    } catch (err) {
      setError(
        (err && err.response && err.response.data && err.response.data.error) ||
          err.message ||
          'Nu am putut actualiza Emag Ads'
      );
    } finally {
      setUpdatingAds((prev) => ({ ...prev, [item.id]: false }));
    }
  };


  const getRowStyle = (item) => {
    const qty = Number(item.quantity || 0);
    const offline = item.offlineFlag === true;

    if (qty > 0 && offline) {
      return {
        background: '#fff7ed'
      };
    }

    return {};
  };

  return (
    <div style={pageStyle}>
      <h1 style={titleStyle}>Stoc & aprovizionare</h1>
      <p style={subtitleStyle}>
        Estimarea tine cont ca marfa ajunge in 15 zile si vrem inca 15 zile buffer dupa sosire.
      </p>

      <div style={statsGridStyle}>
        <StatCard label="Produse totale" value={summary.total} />
        <StatCard label="De comandat" value={summary.needsOrder} />
        <StatCard label="Comanda urgenta" value={summary.urgent} />
        <StatCard label="Fara stoc" value={summary.outOfStock} />
        <StatCard label="Pe drum" value={summary.onTheWay} />
        <StatCard label="Pe drum insuficient" value={summary.onTheWayInsufficient} />
      </div>

      {message ? <div style={successStyle}>{message}</div> : null}
      {error ? <div style={errorStyle}>{error}</div> : null}

      <div style={twoColsStyle}>
        <div style={responsiveCardStyle}>
          <h2 style={sectionTitleStyle}>Adauga produs nou</h2>

          <form onSubmit={handleAddProduct}>
            <div style={formGridStyle}>
              <div>
                <label style={labelStyle}>Cod produs</label>
                <input
                  type="text"
                  value={newProduct.id}
                  onChange={(e) => setNewProduct({ ...newProduct, id: e.target.value })}
                  style={responsiveInputStyle}
                />
              </div>

              <div>
                <label style={labelStyle}>Nume produs</label>
                <input
                  type="text"
                  maxLength={80}
                  value={newProduct.productName}
                  onChange={(e) =>
                    setNewProduct({
                      ...newProduct,
                      productName: e.target.value.slice(0, 80)
                    })
                  }
                  style={responsiveInputStyle}
                />
              </div>

              <div>
                <label style={labelStyle}>Cantitate initiala</label>
                <input
                  type="number"
                  min="0"
                  value={newProduct.quantity}
                  onChange={(e) => setNewProduct({ ...newProduct, quantity: e.target.value })}
                  style={responsiveInputStyle}
                />
              </div>

              <div>
                <label style={labelStyle}>Pe drum</label>
                <input
                  type="number"
                  min="0"
                  value={newProduct.inTransit}
                  onChange={(e) => setNewProduct({ ...newProduct, inTransit: e.target.value })}
                  style={responsiveInputStyle}
                />
              </div>


              <div>
                <label style={labelStyle}>Cod de bare</label>
                <input
                  type="text"
                  value={newProduct.barcode}
                  onChange={(e) => setNewProduct({ ...newProduct, barcode: e.target.value })}
                  style={responsiveInputStyle}
                />
              </div>
            </div>

            <button type="submit" style={buttonStyle}>
              Adauga produs nou
            </button>
          </form>
        </div>

        <div style={responsiveCardStyle}>
          <h2 style={sectionTitleStyle}>Produse de comandat</h2>

          <div style={responsiveTableWrapStyle}>
            <table style={tableStyleCompact}>
              <thead>
                <tr>
                  <th style={thStyleWrap}>Cod</th>
                  <th style={thStyleWrap}>Produs</th>
                  <th style={thStyleWrap}>Stoc</th>
                  <th style={thStyleWrap}>Pe<br />drum</th>
                  <th style={thStyleWrap}>Buffer<br />15z</th>
                  <th style={thStyleWrap}>Recomandat<br />comanda</th>
                  <th style={thStyleWrap}>Comanda</th>
                </tr>
              </thead>
              <tbody>
                {needsOrderItems.length === 0 ? (
                  <tr>
                    <td style={tdStyle} colSpan="10">Nu exista produse care necesita comanda.</td>
                  </tr>
                ) : (
                  needsOrderItems.map((item) => (
                    <tr key={'needs-' + item.id} style={getRowStyle(item)}>
                      <td style={tdStyle}>{item.id}</td>
                      <td style={tdStyle}>{item.productName}</td>
                      <td style={tdStyle}>{item.quantity}</td>
                      <td style={tdStyle}>{item.inTransit || 0}</td>
                      <td style={tdStyle}>{item.demandAfterArrival || 0}</td>
                      <td style={tdStyle}>{item.recommendedOrderQty || 0}</td>
                      <td style={tdStyle}>
                        <div style={quickOrderWrapStyle}>
                          <input
                            type="number"
                            min="1"
                            value={
                              quickOrderQty[item.id] !== undefined
                                ? quickOrderQty[item.id]
                                : item.recommendedOrderQty || ''
                            }
                            onChange={(e) =>
                              setQuickOrderQty((prev) => ({
                                ...prev,
                                [item.id]: e.target.value
                              }))
                            }
                            style={smallInputStyle}
                          />
                          <button
                            type="button"
                            style={smallButtonStyle}
                            onClick={() => handleQuickOrder(item)}
                          >
                            Comanda
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div style={responsiveCardStyle}>
        <div style={filtersGridStyle}>
          <div>
            <label style={labelStyle}>Cautare</label>
            <input
              type="text"
              placeholder="Nume produs / cod / cod de bare"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={responsiveInputStyle}
            />
          </div>

          <div>
            <label style={labelStyle}>Filtru status</label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              style={responsiveInputStyle}
            >
              <option value="all">Toate</option>
              <option value="needsOrder">Doar de comandat</option>
              <option value="OK">OK</option>
              <option value="Stoc mic">Stoc mic</option>
              <option value="Fara stoc">Fara stoc</option>
              <option value="Comanda urgent">Comanda urgent</option>
              <option value="Pe drum">Pe drum</option>
              <option value="Pe drum insuficient">Pe drum insuficient</option>
            </select>
          </div>

          <div>
            <label style={labelStyle}>Sortare</label>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              style={responsiveInputStyle}
            >
              <option value="daysLeftAsc">Zile ramase crescator</option>
              <option value="daysLeftDesc">Zile ramase descrescator</option>
              <option value="stockAsc">Stoc crescator</option>
              <option value="stockDesc">Stoc descrescator</option>
              <option value="salesDesc">Vanzari 10 zile descrescator</option>

              <option value="recommendedDesc">Recomandat comanda descrescator</option>
              <option value="nameAsc">Nume A-Z</option>
              <option value="nameDesc">Nume Z-A</option>
            </select>
          </div>

          <div style={refreshWrapStyle}>
            <button onClick={fetchStock} style={buttonStyle}>
              Reincarca
            </button>
          </div>
        </div>
      </div>

      {loading ? <div style={infoStyle}>Se incarca stocurile...</div> : null}

      <div style={responsiveCardStyle}>
        <div style={responsiveTableWrapStyle}>
          <table style={tableStyleCompact}>
            <thead>
              <tr>
                <th style={thStyleWrap}>Cod<br />produs</th>
                <th style={thStyleWrap}>Nume<br />produs</th>
                  <th style={thStyleWrap}>Emag Ads</th>
                <th style={thStyleWrap}>Stoc<br />actual</th>
                <th style={thStyleWrap}>Pe<br />drum</th>
                <th style={thStyleWrap}>Vanzari<br />10z</th>
                <th style={thStyleWrap}>Buffer<br />15z</th>
                <th style={thStyleWrap}>Recomandat<br />comanda</th>
                <th style={thStyleWrap}>Deficit<br />dupa sosire</th>
                <th style={thStyleWrap}>Zile<br />ramase</th>
                <th style={thStyleWrap}>Cod de<br />bare</th>
                <th style={thStyleWrap}>Offline</th>
                <th style={thStyleWrap}>Status</th>
                <th style={thStyleWrap}>Edit</th>
                <th style={thStyleWrap}>Sterge</th>
              </tr>
            </thead>
            <tbody>
              {!loading && filteredStock.length === 0 ? (
                <tr>
                  <td style={tdStyle} colSpan="15">
                    Nu exista produse care sa corespunda filtrelor.
                  </td>
                </tr>
              ) : (
                filteredStock.map((item) => {
                  const isEditing = editingProductId === item.id;

                  return (
                    <tr key={item.id} style={getRowStyle(item)}>
                      <td style={tdStyle}>{item.id}</td>

                      <td style={tdStyle}>
                        {isEditing ? (
                          <input
                            type="text"
                            maxLength={80}
                            value={editProduct.productName}
                            onChange={(e) =>
                              setEditProduct({
                                ...editProduct,
                                productName: e.target.value.slice(0, 80)
                              })
                            }
                            style={tableInputStyle}
                          />
                        ) : (
                          item.productName
                        )}
                      </td>
                        <td style={tdStyle}>
                          <input
                            type="checkbox"
                            checked={item.emagAds === true}
                            disabled={updatingAds[item.id] === true}
                            onChange={(e) => handleToggleEmagAds(item, e.target.checked)}
                          />
                        </td>

                      <td style={tdStyle}>
                        {isEditing ? (
                          <input
                            type="number"
                            value={editProduct.quantity}
                            onChange={(e) =>
                              setEditProduct({ ...editProduct, quantity: e.target.value })
                            }
                            style={tableInputStyle}
                          />
                        ) : (
                          item.quantity
                        )}
                      </td>

                      <td style={tdStyle}>
                        {isEditing ? (
                          <input
                            type="number"
                            min="0"
                            value={editProduct.inTransit}
                            onChange={(e) =>
                              setEditProduct({ ...editProduct, inTransit: e.target.value })
                            }
                            style={tableInputStyle}
                          />
                        ) : (
                          item.inTransit || 0
                        )}
                      </td>

                      <td style={tdStyle}>{item.salesLast10Days || 0}</td>
                      <td style={tdStyle}>{item.demandAfterArrival || 0}</td>
                      <td style={tdStyle}>{item.recommendedOrderQty || 0}</td>
                      <td style={tdStyle}>{item.deficitAfterTransit || 0}</td>
                      <td style={tdStyle}>{item.daysLeft === null ? '-' : item.daysLeft}</td>


                      <td style={tdStyle}>
                        {isEditing ? (
                          <input
                            type="text"
                            value={editProduct.barcode}
                            onChange={(e) =>
                              setEditProduct({ ...editProduct, barcode: e.target.value })
                            }
                            style={tableInputStyle}
                          />
                        ) : (
                          item.barcode || '-'
                        )}
                      </td>

                      <td style={tdStyle}>
<input
type="checkbox"
checked={!!item.offlineFlag}
disabled={!item.offlineFlag}
title={item.offlineFlag ? 'Scoate bifa dupa ce produsul a fost repus online pe eMAG' : 'Bifa se pune automat cand stocul ajunge la 0'}
onChange={async(e)=>{
 try{
   await api.patch('/toggleOfflineFlag',{
     id:item.id,
     offlineFlag:e.target.checked
   });
   fetchStock();
 }catch(err){
   alert(err?.response?.data?.error || err?.message || 'Eroare salvare');
 }
}}
style={{transform:'scale(1.4)',cursor:item.offlineFlag ? 'pointer' : 'not-allowed'}}
/>
</td>

<td style={tdStyle}>
<span style={getStatusBadgeStyle(item.status)}>{item.status}</span>
</td>

                      <td style={tdStyle}>
                        {isEditing ? (
                          <div style={editActionsWrapStyle}>
                            <button type="button" onClick={saveEditProduct} style={saveButtonStyle}>
                              Salveaza
                            </button>
                            <button type="button" onClick={cancelEditProduct} style={cancelButtonStyle}>
                              Anuleaza
                            </button>
                          </div>
                        ) : (
                          <button
                            type="button"
                            onClick={() => startEditProduct(item)}
                            style={editButtonStyle}
                          >
                            Edit
                          </button>
                        )}
                      </td>

                      <td style={tdStyle}>
                        <button
                          type="button"
                          onClick={() => handleDeleteProduct(item)}
                          style={deleteButtonStyle}
                          title="Sterge produs"
                        >
                          
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function StatCard({ label, value }) {
  return (
    <div style={statCardStyle}>
      <div style={statLabelStyle}>{label}</div>
      <div style={statValueStyle}>{value}</div>
    </div>
  );
}

function getStatusBadgeStyle(status) {
  const baseStyle = {
    display: 'inline-block',
    padding: '6px 10px',
    borderRadius: '999px',
    fontSize: '12px',
    fontWeight: '700'
  };

  if (status === 'OK') {
    return { ...baseStyle, background: '#dcfce7', color: '#166534' };
  }

  if (status === 'Stoc mic') {
    return { ...baseStyle, background: '#fef3c7', color: '#92400e' };
  }

  if (status === 'Fara stoc') {
    return { ...baseStyle, background: '#fee2e2', color: '#991b1b' };
  }

  if (status === 'Comanda urgent') {
    return { ...baseStyle, background: '#fecaca', color: '#7f1d1d' };
  }

  if (status === 'Pe drum') {
    return { ...baseStyle, background: '#dbeafe', color: '#1d4ed8' };
  }

  if (status === 'Pe drum insuficient') {
    return { ...baseStyle, background: '#e9d5ff', color: '#6b21a8' };
  }

  return { ...baseStyle, background: '#e5e7eb', color: '#111827' };
}

const pageStyle = {
  maxWidth: '1650px',
  margin: '0 auto'
};

const titleStyle = {
  marginBottom: '8px',
  fontSize: '32px',
  color: '#111827'
};

const subtitleStyle = {
  marginTop: 0,
  marginBottom: '20px',
  color: '#4b5563'
};

const statsGridStyle = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
  gap: '16px',
  marginBottom: '20px'
};

const statCardStyle = {
  background: '#ffffff',
  borderRadius: '12px',
  padding: '18px',
  boxShadow: '0 2px 10px rgba(0,0,0,0.08)'
};

const statLabelStyle = {
  color: '#6b7280',
  fontSize: '14px',
  marginBottom: '8px'
};

const statValueStyle = {
  color: '#111827',
  fontWeight: '800',
  fontSize: '28px'
};

const cardStyle = {
  background: '#ffffff',
  borderRadius: '12px',
  padding: '20px',
  boxShadow: '0 2px 10px rgba(0,0,0,0.08)',
  marginBottom: '20px'
};

const sectionTitleStyle = {
  marginTop: 0,
  marginBottom: '16px',
  color: '#111827'
};

const twoColsStyle = {
  display: 'grid',
  gridTemplateColumns: '1fr 1.35fr',
  gap: '20px',
  marginBottom: '20px'
};

const formGridStyle = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
  gap: '14px',
  marginBottom: '16px'
};

const filtersGridStyle = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
  gap: '16px',
  alignItems: 'end'
};

const labelStyle = {
  display: 'block',
  marginBottom: '8px',
  fontWeight: '600',
  color: '#111827'
};

const inputStyle = {
  width: '100%',
  padding: '12px',
  fontSize: '15px',
  border: '1px solid #d1d5db',
  borderRadius: '8px',
  boxSizing: 'border-box'
};

const tableInputStyle = {
  width: '100%',
  minWidth: '90px',
  padding: '8px',
  fontSize: '13px',
  border: '1px solid #d1d5db',
  borderRadius: '6px',
  boxSizing: 'border-box'
};

const smallInputStyle = {
  width: '90px',
  padding: '8px',
  fontSize: '14px',
  border: '1px solid #d1d5db',
  borderRadius: '8px'
};

const quickOrderWrapStyle = {
  display: 'flex',
  gap: '8px',
  alignItems: 'center'
};

const editActionsWrapStyle = {
  display: 'flex',
  gap: '6px',
  flexWrap: 'wrap'
};

const refreshWrapStyle = {
  display: 'flex',
  alignItems: 'end'
};

const buttonStyle = {
  background: '#2563eb',
  color: '#ffffff',
  border: 'none',
  borderRadius: '8px',
  padding: '12px 18px',
  fontWeight: '700',
  cursor: 'pointer'
};

const smallButtonStyle = {
  background: '#111827',
  color: '#ffffff',
  border: 'none',
  borderRadius: '8px',
  padding: '8px 12px',
  fontWeight: '700',
  cursor: 'pointer'
};

const editButtonStyle = {
  background: '#2563eb',
  color: '#ffffff',
  border: 'none',
  borderRadius: '8px',
  padding: '8px 12px',
  fontWeight: '700',
  cursor: 'pointer'
};

const saveButtonStyle = {
  background: '#059669',
  color: '#ffffff',
  border: 'none',
  borderRadius: '8px',
  padding: '8px 10px',
  fontWeight: '700',
  cursor: 'pointer'
};

const cancelButtonStyle = {
  background: '#6b7280',
  color: '#ffffff',
  border: 'none',
  borderRadius: '8px',
  padding: '8px 10px',
  fontWeight: '700',
  cursor: 'pointer'
};

const deleteButtonStyle = {
  background: '#dc2626',
  color: '#ffffff',
  border: 'none',
  borderRadius: '8px',
  width: '32px',
  height: '32px',
  fontSize: '20px',
  lineHeight: '20px',
  fontWeight: '700',
  cursor: 'pointer'
};

const infoStyle = {
  background: '#e0f2fe',
  color: '#075985',
  padding: '12px 16px',
  borderRadius: '10px',
  marginBottom: '16px',
  fontWeight: '600'
};

const successStyle = {
  background: '#dcfce7',
  color: '#166534',
  padding: '12px 16px',
  borderRadius: '10px',
  marginBottom: '16px',
  fontWeight: '600'
};

const errorStyle = {
  background: '#fee2e2',
  color: '#991b1b',
  padding: '12px 16px',
  borderRadius: '10px',
  marginBottom: '16px',
  fontWeight: '600'
};

const tableWrapStyle = {
  overflowX: 'auto'
};

const tableStyleCompact = {
  width: '100%',
  borderCollapse: 'collapse',
  minWidth: '1280px'
};

const thStyleWrap = {
  textAlign: 'left',
  padding: '10px',
  background: '#f3f4f6',
  borderBottom: '1px solid #e5e7eb',
  color: '#111827',
  fontSize: '13px',
  whiteSpace: 'normal',
    lineHeight: 1.15,
  position: 'sticky',
  top: 0,
  zIndex: 30,
  background: '#f9fafb',
  boxShadow: '0 1px 0 #d1d5db'
};

const tdStyle = {
  padding: '10px',
  borderBottom: '1px solid #e5e7eb',
  fontSize: '13px',
  color: '#111827',
  whiteSpace: 'nowrap',
  verticalAlign: 'top'
};

export default Stock;
